import { NavEvent } from '../features/nav/NavEvent';
import { analyticsService } from './AnalyticsService';

/**
 * Page View Tracker for navigation events
 * Integrates with the existing NavEvent-based navigation system
 */
export class PageViewTracker {
  private currentPage: NavEvent | null = null;
  private sessionStartTime: number = Date.now();
  private pageStartTime: number = Date.now();

  /**
     * Custom page title mappings for analytics
     */
  private readonly pageTitleMappings: Record<NavEvent, string> = {
    [NavEvent.ABOUT]: 'About - Sailing Handicap Calculator',
    [NavEvent.GROUPS]: 'Groups - Sailing Handicap Calculator',
    [NavEvent.BOATS]: 'Boats - Sailing Handicap Calculator',
    [NavEvent.TABLE]: 'Handicap Table - Sailing Handicap Calculator',
    [NavEvent.IMPORT]: 'Import Data - Sailing Handicap Calculator',
    [NavEvent.EXPORT]: 'Export Data - Sailing Handicap Calculator',
  };

  /**
     * Track a page view event
     * @param {NavEvent} page - The page being viewed
     */
  trackPageView(page: NavEvent): void {
    const now = Date.now();
    const previousPage = this.currentPage;
    const sessionDuration = now - this.sessionStartTime;
    const pageDuration = previousPage ? now - this.pageStartTime : 0;

    try {
      // Log page view event
      analyticsService.logEvent('page_view', {
        page_title: this.pageTitleMappings[page],
        page_location: this.getPageLocation(page),
        previous_page: previousPage ? this.pageTitleMappings[previousPage] : undefined,
        session_duration: Math.round(sessionDuration / 1000), // Convert to seconds
        page_duration: previousPage ? Math.round(pageDuration / 1000) : undefined,
        navigation_type: previousPage ? 'internal' : 'initial',
      });
    } catch (error) {
      console.error('PageViewTracker: Error tracking page view:', error);
    }

    // Update tracking state regardless of analytics success
    this.currentPage = page;
    this.pageStartTime = now;

    console.log(`PageViewTracker: Tracked page view - ${page}`);
  }

  /**
     * Track feature usage when accessing specific functionality
     * @param {NavEvent} page - The page/feature being accessed
     * @param {string} featureName - Specific feature name
     */
  trackFeatureUsage(page: NavEvent, featureName: string): void {
    try {
      analyticsService.logEvent('feature_usage', {
        feature_name: featureName,
        page_context: this.pageTitleMappings[page],
        page_location: this.getPageLocation(page),
        session_duration: Math.round((Date.now() - this.sessionStartTime) / 1000),
      });

      console.log(`PageViewTracker: Tracked feature usage - ${featureName} on ${page}`);
    } catch (error) {
      console.error('PageViewTracker: Error tracking feature usage:', error);
    }
  }

  /**
     * Get the current page being tracked
     * @return {NavEvent | null} Current page or null if none set
     */
  getCurrentPage(): NavEvent | null {
    return this.currentPage;
  }

  /**
     * Get session duration in seconds
     * @return {number} Session duration in seconds
     */
  getSessionDuration(): number {
    return Math.round((Date.now() - this.sessionStartTime) / 1000);
  }

  /**
     * Reset session tracking (useful for testing or session restart)
     */
  resetSession(): void {
    this.sessionStartTime = Date.now();
    this.pageStartTime = Date.now();
    this.currentPage = null;
    console.log('PageViewTracker: Session reset');
  }

  /**
     * Generate page location string for analytics
     * @param {NavEvent} page - The page to get location for
     * @return {string} Page location string
     */
  private getPageLocation(page: NavEvent): string {
    // Since this app doesn't use URL routing, we'll create virtual paths
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://sailing-handicap-calculator.web.app';
    return `${baseUrl}/${page}`;
  }
}

// Export singleton instance
export const pageViewTracker = new PageViewTracker();
export default pageViewTracker;
